import { useQuery } from '@apollo/client';
import { AddCircle, VerifiedUser } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useTabs } from '../lib/tabs';
import { RoleEventTypeListDocument } from './RoleEventTypeList.generated';
import { AddEventTypesDialog, BulkActionsMenu, EventTypeTable } from './components';

export const RoleEventTypeList = () => {
  const { t } = useTranslation();
  const params = useParams<{ roleId: string }>();
  const roleId = parseInt(params.roleId ?? '');

  const { currentNetwork } = useAppContext();
  const tabs = useTabs();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading, error } = useQuery(RoleEventTypeListDocument, {
    variables: { networkId: currentNetwork.id, roleId, search },
  });

  const [addEventTypesOpen, setAddEventTypesOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  // Update selection if data changes...this seems overly complicated but works
  useEffect(() => {
    if (!data?.network?.role.eventTypeRules) return;
    const eventTypeRuleIds = new Set<GridRowId>(
      data.network.role.eventTypeRules.map((eventTypeRule) => eventTypeRule.id),
    );
    if (selectedIds.every((id) => eventTypeRuleIds.has(id))) return;
    setSelectedIds(selectedIds.filter((id) => eventTypeRuleIds.has(id)));
  }, [data?.network?.role.eventTypeRules, selectedIds]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  const eventTypeRules = data?.network?.role.eventTypeRules ?? [];

  return (
    <>
      {data?.network && (
        <>
          <Toolbar
            actions={
              <Button
                color="primary"
                onClick={() => setAddEventTypesOpen(true)}
                startIcon={<AddCircle />}
                variant="contained"
              >
                {t('pages.roles.buttons.add_event_types')}
              </Button>
            }
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<VerifiedUser />}
            titleText={data.network.role.name}
          />

          <ScrollableRouterTabs current={t('pages.roles.tabs.event_types')} tabs={tabs} />
          <PageContainer>
            <SearchContainer>
              <SearchBar
                placeholder={t('pages.roles.event_types.search_placeholder')}
                search={search}
              />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                <BulkActionsMenu role={data.network.role} selectedIds={selectedIds} />
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={eventTypeRules.length}
                  thing="pages.roles.event_types.thing_plural"
                />
              </Box>
            </SearchContainer>
            <EventTypeTable
              checkboxSelection={eventTypeRules.length > 1}
              disableRowSelectionOnClick={eventTypeRules.length < 2}
              loading={loading}
              onRowSelectionModelChange={setSelectedIds}
              role={data.network.role}
              rows={data.network.role.eventTypeRules}
              rowSelectionModel={selectedIds}
            />
          </PageContainer>

          {addEventTypesOpen && (
            <AddEventTypesDialog
              fullWidth
              onClose={() => setAddEventTypesOpen(false)}
              open
              role={data.network.role}
              eventTypes={data.network.allEventTypes}
            />
          )}
        </>
      )}
    </>
  );
};
